import React from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import {
  CustomFieldDefinition,
  FieldValueType,
} from '@teetsh/app/src/types/customField';
import { BoolCustomField } from './BoolCustomField';
import { TextCustomField } from './TextCustomField';
import { SelectCustomField } from '@teetsh/app/src/components/NewCustomFields/SelectCustomField';
import { Option } from './types';
import _isArray from 'lodash-es/isArray';
import { InlineFormGroup } from '@teetsh/app/src/components/NewCustomFields/InlineFormGroup';
import { NumberCustomField } from './NumberCustomField';
import { PhoneCustomField } from './PhoneCustomField';
import { Attachment } from 'types/attachment';
import { FilesAndMediaCell } from 'containers/Eleves/List/Table/Cells/FilesAndMediaCell';
import { MultiselectCustomField } from './MultiselectCustomField';

interface Props {
  customFieldDefinition: CustomFieldDefinition;
  withoutBorders?: boolean;
  value?: FieldValueType;
  options: Option[];
  onUpdateCustomField: (v: CustomFieldDefinition) => Promise<any>;
  onChange: (v: FieldValueType) => void;
}

function NewCustomFieldsSwitcher(props: Props) {
  const {
    customFieldDefinition,
    value,
    onChange,
    options,
    onUpdateCustomField,
    withoutBorders,
  } = props;

  switch (customFieldDefinition.type) {
    case 'bool':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          center
          // ON ne revient pas à la ligne dans le cas des checkbox, sinon c'est bizarre
          tw="flex gap-x-8 items-center sm:grid"
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <BoolCustomField
            name={customFieldDefinition.name}
            value={value as boolean}
            onChange={onChange}
          />
        </InlineFormGroup>
      );

    case 'text':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <TextCustomField value={value as string} onChange={onChange} />
        </InlineFormGroup>
      );

    case 'number':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <NumberCustomField value={value as number} onChange={onChange} />
        </InlineFormGroup>
      );

    case 'filesAndMedia':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <FilesAndMediaCell
            value={value as Attachment[]}
            onChange={onChange}
          />
        </InlineFormGroup>
      );

    case 'phone':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <PhoneCustomField
            id={customFieldDefinition.id}
            value={value as string}
            onChange={onChange}
          />
        </InlineFormGroup>
      );

    case 'multiselect':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <MultiselectCustomField
            value={(value || []) as string[]}
            onChange={onChange}
            onCreateOption={v =>
              onUpdateCustomField({
                ...customFieldDefinition,
                options: [
                  ...customFieldDefinition.options,
                  { id: uuid(), value: v },
                ],
              })
            }
            onRemoveOption={v =>
              onUpdateCustomField({
                ...customFieldDefinition,
                options: customFieldDefinition.options.filter(
                  opt => opt.value !== v,
                ),
              })
            }
            options={customFieldDefinition.options}
          />
        </InlineFormGroup>
      );

    case 'select':
      return (
        <InlineFormGroup
          withoutBorders={withoutBorders}
          key={customFieldDefinition.id}
          onChange={onUpdateCustomField}
          value={customFieldDefinition}
        >
          <SelectCustomField
            name={customFieldDefinition.name}
            options={options}
            value={(_isArray(value) ? value : [value]) as string[]}
            allowMultipleValue
            onChange={onChange}
            onRemoveOption={id =>
              onUpdateCustomField({
                ...customFieldDefinition,
                options: customFieldDefinition.options.filter(
                  opt => opt.id !== id,
                ),
              })
            }
            onUpdateOption={v =>
              onUpdateCustomField({
                ...customFieldDefinition,
                options: customFieldDefinition.options.map(opt =>
                  opt.id === v.id
                    ? {
                        value: v.value,
                        id: v.id,
                        color: v.color,
                        position: opt.position,
                      }
                    : opt,
                ),
              })
            }
            onCreateOption={({ optionValue, color }) => {
              const optionId = uuid();
              const newOption = {
                id: optionId,
                value: optionValue,
                color: color || 'red-400',
                position: 1,
              };
              return onUpdateCustomField({
                ...customFieldDefinition,
                options: [...customFieldDefinition.options, newOption],
              }).then(() =>
                // en DB on a parfois un string et parfois un array car avant ce champ ne pouvait avoir qu'une seule valeur
                // on doit donc être rétro compatible avec ces 2 cas
                onChange(
                  _isArray(value)
                    ? [...(value as string[]), optionId]
                    : [value as string, optionId],
                ),
              );
            }}
          />
        </InlineFormGroup>
      );
  }
}

export function NewCustomFields(props: Props) {
  return <NewCustomFieldsSwitcher {...props} />;
}
